/**
 * ScoutDegradedBanner — calm advisory shown above SignalReview when the
 * Scout run fell back to deterministic classification (OpenAI quota
 * exhausted, fallback providers also out, or no LLM keys configured).
 *
 * Two-tier message:
 *   - Admins (integration:view) see the full provider context including
 *     which providers were attempted.
 *   - Operators / Viewers see a softer message without provider internals.
 *
 * Rendering rules:
 *   - Hidden if the run was not degraded.
 *   - Hidden if no warnings were attached to the response.
 *   - Single warning shown (de-duped server-side by `kind`).
 */
import React from 'react';
import { useAuth } from '../context/AuthContext';

export default function ScoutDegradedBanner({ warnings, degraded, diagnostics }) {
  const { hasPermission } = useAuth();
  const isAdmin = hasPermission && (hasPermission('integration:view') || hasPermission('integration:manage'));

  const showWarning = degraded && Array.isArray(warnings) && warnings.length > 0;
  const search = diagnostics?.search;
  const classification = diagnostics?.classification;
  const showEvidence = isAdmin && (search?.version || classification?.version);
  if (!showWarning && !showEvidence) return null;
  const w = showWarning ? warnings[0] : {};

  const tone = w.kind === 'provider_unavailable'
    ? { bg: '#fee2e2', border: '#fecaca', dot: '#b91c1c', text: '#991b1b' }
    : { bg: '#fef3c7', border: '#fde68a', dot: '#b45309', text: '#92400e' };

  const message = showWarning ? (isAdmin ? w.admin_message : w.user_message) : 'Scout execution evidence';
  const attempts = isAdmin ? (w.details?.provider_attempts || []) : [];

  return (
    <div
      data-testid="scout-degraded-banner"
      role="status"
      aria-live="polite"
      style={{
        margin: '8px 0 12px', padding: '10px 14px', borderRadius: 6,
        background: tone.bg, border: `1px solid ${tone.border}`, fontSize: 13, color: tone.text,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span aria-hidden style={{
          width: 8, height: 8, borderRadius: '50%', background: tone.dot, flex: '0 0 auto',
        }} />
        <span style={{ flex: 1 }}>{message}</span>
      </div>
      {attempts.length > 0 && (
        <div data-testid="scout-degraded-attempts" style={{
          marginTop: 8, paddingTop: 8, borderTop: `1px solid ${tone.border}`,
          fontSize: 11, color: tone.text, opacity: 0.9,
        }}>
          <span style={{ fontWeight: 600 }}>Provider chain:</span>{' '}
          {attempts.map((a, i) => (
            <span key={i} style={{ marginRight: 10 }}>
              {a.provider} → {a.success ? 'ok' : (a.error_type || 'failed')}
              {a.fallback_used ? ' (fallback)' : ''}
            </span>
          ))}
        </div>
      )}
      {showEvidence && <details data-testid="scout-execution-evidence" style={{ marginTop: 8 }}>
        <summary>Search and classification provenance</summary>
        {search?.version && <p data-testid="scout-search-evidence">
          Search: {search.search_provider} · {search.search_status} · {search.provider_requests ?? 'unknown'} observed request(s){search.provider_requests_complete === false ? ' (incomplete count)' : ''} · {search.returned_result_count} returned result(s) · {search.provider_errors ?? 'unknown'} observed failure(s).
          {search.search_status === 'cache_hit' && ' Saved search cache; no fresh provider request.'}
          {search.search_status === 'empty' && ' Search succeeded with no results.'}
          {search.search_status === 'unverified_empty' && ' Empty outcome is not verified as a successful search.'}
          {search.search_status === 'partial' && ' Partial search: some requests failed or were locally blocked.'}
          {search.search_status === 'failed' && ' Search failed or was locally blocked; this is not a clean no-results outcome.'}
          {search.last_provider_result && <> Last search outcome: {search.last_provider_result.code || search.last_provider_result.status}{search.last_provider_result.http_status ? ` · HTTP ${search.last_provider_result.http_status}` : ''}{search.last_provider_result.dispatched === false ? ' · not dispatched' : ''}.</>}
        </p>}
        {classification?.version && <div data-testid="scout-classification-evidence">
          Classification: {classification.status}. This describes execution, not factual support.
          {(classification.provider_attempts || []).map((a, i) => <p key={i}>
            {a.provider} / {a.resolved_model || a.model || 'model not recorded'} · {a.success ? 'response received' : (a.error_type || 'failed')}{a.fallback_used ? ' · fallback' : ''}
            {a.request_id ? ` · request ${a.request_id}` : ''}{a.duration_ms != null ? ` · ${a.duration_ms}ms` : ''}
            {a.input_tokens != null ? ` · input ${a.input_tokens}` : ''}{a.output_tokens != null ? ` · output ${a.output_tokens}` : ''}
          </p>)}
        </div>}
      </details>}
    </div>
  );
}
